import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injectable, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { Device } from '@ionic-native/device/ngx';
import { FCM } from 'cordova-plugin-fcm-with-dependecy-updated/ionic/ngx';
import { FCM as fcm } from 'cordova-plugin-fcm-with-dependecy-updated/ionic';
import { File } from '@ionic-native/file/ngx';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';
import { WebView } from '@ionic-native/ionic-webview/ngx';
import { LocalNotifications } from '@ionic-native/local-notifications/ngx';
import { Network } from '@ionic-native/network/ngx';
import { SafariViewController } from '@ionic-native/safari-view-controller/ngx';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { Event } from '@sentry/browser';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgxIonicImageViewerModule } from 'ngx-ionic-image-viewer';
import { APP_STORE_ID, APP_VERSION, PACKAGE_ID, TENANT_ID, TENANT_NAME } from '../config';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppConfigProvider } from './app.config';
import { AlertService } from './services/alert/alert.service';
import { ConnectService } from './services/connect/connect.service';
import { ConnectivityService } from './services/connectivity/connectivity.service';
import { FingerPrintService } from './services/finger-print/finger-print.service';
import { PhotoVideoService } from './services/photo-video/photo-video.service';
import { PushNotificationService } from './services/push-notifications/push-notification.service';
import { TermsConditionsService } from './services/static/terms-conditions.service';
import { storageMigrationFactory } from './services/storage-legacy/storage-migration';
import { StorageLegacyModule } from './services/storage-legacy/storage.module';
import { StorageLegacyService } from './services/storage-legacy/storage.service';
import { StorageModule } from './services/storage/storage.module';
import { StorageService } from './services/storage/storage.service';
import { TranslateConfigService } from './services/translate/translate-config.service';
import { UpdateAppModule } from './update-app/update-app.module';
import { CustomTranslationLoader } from './utilities/translate-loader';
import { UploadService } from './services/upload/upload.service';
import { LiveUpdateModule } from './modals/live-update/live-update.module';
import { VaultService } from './services/vault/vault.service';
import { KeyValueStorage } from '@ionic-enterprise/secure-storage/dist/ngx';
import { PinCodeModule } from './components/pin-code/pin-code.module';
import { WeguideHttpInterceptor } from './services/interceptor/http.interceptor';
import { Deploy } from 'cordova-plugin-ionic/dist/ngx';
import { ShortURLModule } from './short-url/short-url.module';
import { DeepLinkInitializer } from './initializers/deeplink';
import { NoopComponent } from './components/noop.component';

export function languageLoader(http: HttpClient) {
  return new CustomTranslationLoader(http, 'assets/weguide-medical-frontend-i18n/', '.json');
}

/* istanbul ignore next */
export function appInitializerFactory(translateConfigService: TranslateConfigService) {
  return async () => {
    const language = await translateConfigService.getLanguage();
    await translateConfigService.setLanguage(language);
  };
}
const denyUrls = ['/verification'];
const ignoredErrors = ['ChunkLoadError', 'isProxied', 'ResizeObserver', /Loading chunk [\d]+ failed/];
/* istanbul ignore next */
Sentry.init({
  dsn: 'https://9e44c95ac6ea4790a3ae846f7096658c@o58028.ingest.sentry.io/1847228',
  release: `${PACKAGE_ID}@${APP_VERSION}`,
  environment: environment.name,
  enabled: environment.production,
  integrations: [new BrowserTracing()],
  tracesSampleRate: 0.25,
  ignoreErrors: ignoredErrors,
  beforeSend(event: Event) {
    denyUrls.forEach(url => {
      checkAndSetUrl(event.request, 'url', url);
      checkAndSetUrl(event.request?.headers, 'Referer', url);
      event.breadcrumbs?.forEach(breadcrumb => {
        if (breadcrumb.category === 'navigation') {
          checkAndSetUrl(breadcrumb.data, 'from', url);
          checkAndSetUrl(breadcrumb.data, 'to', url);
        }
      });
    });
    function checkAndSetUrl<T>(obj: T | undefined, key: keyof T, matcher: string) {
      if (obj) {
        const value = obj[key];
        if (typeof value === 'string') {
          const index = value.indexOf(matcher);
          if (index !== -1) {
            delete obj[key];
          }
        }
      }
    }
    return event;
  },
  denyUrls,
});

/* istanbul ignore next */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  constructor() {}
  handleError(error) {
    Sentry.captureException(error.originalError || error);
    console.error(error);
  }
}

@NgModule({
  declarations: [AppComponent, NoopComponent],
  entryComponents: [],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonicModule.forRoot({
      swipeBackEnabled: false,
    }),
    AppRoutingModule,
    HttpClientModule,
    StorageModule.forRoot({ name: `${TENANT_ID}_${environment.name}_storage` }),
    StorageLegacyModule.forRoot({ name: 'castor_storage' }, { name: '_WeGuideMedical' }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: languageLoader,
        deps: [HttpClient],
      },
    }),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    NgxIonicImageViewerModule,
    UpdateAppModule.forRoot({
      key: `${TENANT_ID}_${environment.name}_versions`,
      appName: TENANT_NAME,
      appVersion: APP_VERSION,
      deployChannel: environment.name,
      packageId: PACKAGE_ID,
      appStoreId: APP_STORE_ID,
      unsafeUrls: ['/questionnaire'],
      intervalDuration: environment.updateInterval,
    }),
    AngularSvgIconModule.forRoot(),
    LiveUpdateModule,
    PinCodeModule,
    ShortURLModule,
  ],
  providers: [
    AppConfigProvider,
    {
      provide: APP_INITIALIZER,
      useFactory: storageMigrationFactory,
      deps: [StorageService, StorageLegacyService, VaultService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateConfigService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WeguideHttpInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: environment.production ? /* istanbul ignore next */ SentryErrorHandler : ErrorHandler },
    StatusBar,
    FingerPrintService,
    Network,
    SplashScreen,
    ConnectService,
    UploadService,
    AlertService,
    TranslateConfigService,
    Device,
    PushNotificationService,
    LocalNotifications,
    { provide: FCM, useValue: fcm },
    AppVersion,
    TermsConditionsService,
    ConnectivityService,
    AndroidPermissions,
    PhotoVideoService,
    File,
    WebView,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    InAppBrowser,
    SafariViewController,
    KeyValueStorage,
    Title,
    Deploy,
    DeepLinkInitializer,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
